// Cinema/Film Studio Portfolio page — monolithic iframe block (p_5)
// Multi-brand: capital / atelier / digital / form

export type CinemaPortfolioBrand = 'capital' | 'atelier' | 'digital' | 'form'
export interface CinemaPortfolioPageData { brand?: CinemaPortfolioBrand }
export const DEFAULT_CINEMA_PORTFOLIO_DATA: CinemaPortfolioPageData = { brand: 'capital' }

// ── CSS ───────────────────────────────────────────────────────────────────────
const CINEMA_PORTFOLIO_CSS = `
@import url('https://cdn.jsdelivr.net/npm/@fontsource/archivo@5/500.css');
@import url('https://cdn.jsdelivr.net/npm/@fontsource/archivo@5/700.css');
@import url('https://cdn.jsdelivr.net/npm/@fontsource/archivo@5/800.css');
@import url('https://cdn.jsdelivr.net/npm/@fontsource/inter@5/400.css');
@import url('https://cdn.jsdelivr.net/npm/@fontsource/inter@5/500.css');
@import url('https://cdn.jsdelivr.net/npm/@fontsource/inter@5/600.css');
@import url('https://cdn.jsdelivr.net/npm/@fontsource/playfair-display@5/500.css');
@import url('https://cdn.jsdelivr.net/npm/@fontsource/playfair-display@5/600.css');
@import url('https://cdn.jsdelivr.net/npm/@fontsource/playfair-display@5/500-italic.css');
@import url('https://cdn.jsdelivr.net/npm/@fontsource/playfair-display@5/600-italic.css');
@import url('https://cdn.jsdelivr.net/npm/@fontsource/jost@5/400.css');
@import url('https://cdn.jsdelivr.net/npm/@fontsource/jost@5/500.css');
@import url('https://cdn.jsdelivr.net/npm/@fontsource/space-grotesk@5/500.css');
@import url('https://cdn.jsdelivr.net/npm/@fontsource/space-grotesk@5/700.css');
@import url('https://cdn.jsdelivr.net/npm/@fontsource/syne@5/600.css');
@import url('https://cdn.jsdelivr.net/npm/@fontsource/syne@5/700.css');
@import url('https://cdn.jsdelivr.net/npm/@fontsource/syne@5/800.css');
@import url('https://cdn.jsdelivr.net/npm/@fontsource/manrope@5/400.css');
@import url('https://cdn.jsdelivr.net/npm/@fontsource/manrope@5/500.css');
@import url('https://cdn.jsdelivr.net/npm/@fontsource/manrope@5/600.css');
@import url('https://cdn.jsdelivr.net/npm/@fontsource/manrope@5/700.css');
@import url('https://cdn.jsdelivr.net/npm/@fontsource/ibm-plex-mono@5/400.css');
@import url('https://cdn.jsdelivr.net/npm/@fontsource/ibm-plex-mono@5/500.css');

/* ============ БАЗА ============ */
*,*::before,*::after{margin:0;padding:0;box-sizing:border-box}
:root{
  --maxw:1340px;
  --pad:clamp(20px,4.5vw,56px);
  --ease:cubic-bezier(.22,.61,.2,1);
  --hdr-h:76px;
}
html{scroll-behavior:smooth}
body{
  font-family:var(--font-b);
  background:var(--bg);
  color:var(--ink);
  line-height:1.55;
  -webkit-font-smoothing:antialiased;
  transition:background .5s ease,color .5s ease;
  overflow-x:hidden;
}
img{display:block;max-width:100%}
a{color:inherit;text-decoration:none}
ul,ol{list-style:none}
button{font:inherit;color:inherit;background:none;border:0;cursor:pointer}
::selection{background:var(--accent);color:#fff}
:focus-visible{outline:2px solid var(--accent);outline-offset:3px}
section{scroll-margin-top:calc(var(--hdr-h) + 12px)}
.wrap{max-width:var(--maxw);margin:0 auto;padding-inline:var(--pad)}
.mono{font-family:'IBM Plex Mono',monospace;font-size:11px;letter-spacing:.14em;text-transform:uppercase}

/* ============ ТЕМЫ / БРЕНДЫ ============ */
body[data-theme="capital"]{
  --bg:#F4F5F7; --surface:#FFFFFF; --ink:#0B1D33; --muted:#5B6572;
  --line:rgba(11,29,51,.15); --accent:#0F3ECC; --accent-soft:rgba(15,62,204,.08);
  --panel:#0B1D33; --panel-ink:#F4F5F7;
  --radius:4px; --btn-r:2px;
  --font-d:'Archivo',sans-serif; --font-b:'Inter',sans-serif;
}
body[data-theme="atelier"]{
  --bg:#F3EEE4; --surface:#FAF7F0; --ink:#191512; --muted:#7E7466;
  --line:rgba(25,21,18,.18); --accent:#A87E3F; --accent-soft:rgba(168,126,63,.12);
  --panel:#191512; --panel-ink:#F3EEE4;
  --radius:0px; --btn-r:999px;
  --font-d:'Playfair Display',serif; --font-b:'Jost',sans-serif;
}
body[data-theme="digital"]{
  --bg:#F6F7F9; --surface:#FFFFFF; --ink:#0B0C0E; --muted:#60656D;
  --line:rgba(11,12,14,.13); --accent:#2547F4; --accent-soft:rgba(37,71,244,.08);
  --panel:#0B0C0E; --panel-ink:#F6F7F9;
  --radius:16px; --btn-r:12px;
  --font-d:'Space Grotesk',sans-serif; --font-b:'Manrope',sans-serif;
}
body[data-theme="form"]{
  --bg:#E9E5DD; --surface:#F2EFE8; --ink:#1B1813; --muted:#7A7263;
  --line:rgba(27,24,19,.18); --accent:#B4502A; --accent-soft:rgba(180,80,42,.1);
  --panel:#1B1813; --panel-ink:#E9E5DD;
  --radius:0px; --btn-r:0px;
  --font-d:'Syne',sans-serif; --font-b:'Manrope',sans-serif;
}
body[data-theme="atelier"] .hero-title em,
body[data-theme="atelier"] .cta-title em{font-style:italic}
body[data-theme="form"] .hero-title,
body[data-theme="form"] .cta-title{text-transform:uppercase;letter-spacing:.005em}
body[data-theme="atelier"] .brand-mark{font-weight:600;letter-spacing:.02em}
body[data-theme="capital"] .brand-mark{font-weight:800}
body[data-theme="digital"] .brand-mark{font-weight:700}
body[data-theme="form"] .brand-mark{font-weight:800}

/* ============ ШУМ ============ */
body::after{
  content:"";position:fixed;inset:0;z-index:2000;pointer-events:none;opacity:.05;
  background-image:url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='.8' numOctaves='2'/></filter><rect width='140' height='140' filter='url(%23n)'/></svg>");
}

/* ============ ТИПОГРАФИКА ============ */
.kicker{color:var(--muted)}
.kicker::before{content:"— ";color:var(--accent)}
h1,h2,h3{font-family:var(--font-d);font-weight:700;line-height:1.04;letter-spacing:-.015em}
.hero-title{font-size:clamp(44px,7.6vw,108px);line-height:.99;letter-spacing:-.025em}
.hero-title em,.cta-title em{font-style:normal;color:var(--accent)}
.sec-title{font-size:clamp(30px,4.6vw,60px)}
.reveal-lines .ln{display:block;overflow:hidden;padding-bottom:.08em;margin-bottom:-.08em}
.reveal-lines .ln>span{display:inline-block;transform:none;transition:transform .95s var(--ease);transition-delay:calc(var(--d,0)*.1s)}
.js .reveal-lines:not(.in) .ln>span{transform:translateY(118%)}

/* ============ REVEAL ============ */
.js [data-observe]{opacity:0;transform:translateY(26px);transition:opacity .85s var(--ease),transform .85s var(--ease)}
.js [data-observe].in{opacity:1;transform:none}

/* ============ КНОПКИ ============ */
.btn{
  display:inline-flex;align-items:center;gap:10px;
  font-family:var(--font-b);font-weight:600;font-size:14px;letter-spacing:.02em;
  padding:15px 28px;border:1px solid var(--ink);border-radius:var(--btn-r);
  transition:background .3s,color .3s,border-color .3s,transform .3s;
  white-space:nowrap;
}
.btn:hover{transform:translateY(-2px)}
.btn-solid{background:var(--ink);color:var(--bg)}
.btn-solid:hover{background:var(--accent);border-color:var(--accent);color:#fff}
.btn-ghost:hover{background:var(--ink);color:var(--bg)}
.btn-accent{background:var(--accent);border-color:var(--accent);color:#fff}
.btn-accent:hover{background:transparent;color:var(--panel-ink)}

/* ============ HEADER ============ */
#hdr{position:fixed;inset:0 0 auto;z-index:1000;transition:background .4s,border-color .4s;border-bottom:1px solid transparent}
#hdr.scrolled{background:color-mix(in srgb,var(--bg) 88%,transparent);backdrop-filter:blur(12px);border-color:var(--line)}
.hdr-in{max-width:var(--maxw);margin:0 auto;padding-inline:var(--pad);height:var(--hdr-h);display:flex;align-items:center;gap:22px}
.brand{display:flex;align-items:baseline;gap:8px;margin-right:auto}
.brand-mark{font-family:var(--font-d);font-size:24px;letter-spacing:-.01em}
.brand-dot{color:var(--accent)}
.brand-sub{font-family:'IBM Plex Mono',monospace;font-size:11px;letter-spacing:.16em;text-transform:uppercase;color:var(--muted)}
.nav-desktop{display:flex;gap:28px}
.nav-desktop a{font-size:14px;font-weight:500;color:var(--muted);position:relative;transition:color .3s}
.nav-desktop a::after{content:"";position:absolute;left:0;bottom:-4px;width:100%;height:1px;background:var(--accent);transform:scaleX(0);transform-origin:right;transition:transform .35s var(--ease)}
.nav-desktop a:hover{color:var(--ink)}
.nav-desktop a:hover::after{transform:scaleX(1);transform-origin:left}
.hdr-right{display:flex;align-items:center;gap:14px}
.switch{display:flex;border:1px solid var(--line);border-radius:999px;overflow:hidden;background:var(--surface)}
.switch-btn{display:flex;align-items:center;gap:6px;padding:8px 11px;font-family:'IBM Plex Mono',monospace;font-size:11px;letter-spacing:.08em;color:var(--muted);transition:background .3s,color .3s;border-right:1px solid var(--line)}
.switch-btn:last-child{border-right:0}
.switch-btn b{font-weight:500}
.switch-btn:hover{color:var(--ink)}
.switch-btn.on{background:var(--ink);color:var(--bg)}
.hdr-cta{padding:11px 20px;font-size:13px}
.burger{display:none;width:42px;height:42px;position:relative;z-index:1600}
.burger span{position:absolute;left:9px;right:9px;height:2px;background:var(--ink);transition:transform .4s var(--ease),top .4s}
.burger span:nth-child(1){top:16px}
.burger span:nth-child(2){top:25px}
body.menu-open .burger span:nth-child(1){top:20px;transform:rotate(45deg)}
body.menu-open .burger span:nth-child(2){top:20px;transform:rotate(-45deg)}
body.menu-open .burger span{background:var(--ink)}

/* ============ МОБИЛЬНОЕ МЕНЮ ============ */
.m-menu{position:fixed;inset:0;z-index:1500;background:var(--bg);transform:translateY(-102%);transition:transform .6s var(--ease);display:flex;flex-direction:column;padding:calc(var(--hdr-h) + 20px) var(--pad) 34px;overflow-y:auto}
.m-menu.open{transform:none}
.m-nav{display:flex;flex-direction:column}
.m-nav a{font-family:var(--font-d);font-size:clamp(30px,8vw,44px);font-weight:700;padding:14px 0;border-bottom:1px solid var(--line);display:flex;justify-content:space-between;align-items:center;opacity:0;transform:translateY(18px);transition:opacity .5s,transform .5s var(--ease)}
.m-menu.open .m-nav a{opacity:1;transform:none}
.m-nav a i{font-style:normal;font-family:'IBM Plex Mono',monospace;font-size:11px;color:var(--accent)}
.m-nav a:nth-child(1){transition-delay:.12s}.m-nav a:nth-child(2){transition-delay:.18s}
.m-nav a:nth-child(3){transition-delay:.24s}.m-nav a:nth-child(4){transition-delay:.3s}
.m-switch{margin:26px 0 22px}
.m-contact{font-family:'IBM Plex Mono',monospace;font-size:12px;letter-spacing:.1em;color:var(--muted);line-height:2}
.m-brand{margin-top:auto;font-family:var(--font-d);font-weight:800;font-size:64px;opacity:.12;line-height:1}

/* ============ HERO ============ */
.hero{min-height:100svh;display:flex;align-items:center;padding:calc(var(--hdr-h) + 30px) 0 40px;position:relative}
.hero-grid{display:grid;grid-template-columns:minmax(0,7fr) minmax(0,5fr);gap:clamp(28px,4vw,70px);align-items:center;width:100%}
.hero-copy .kicker{display:block;margin-bottom:26px}
.hero-sub{margin-top:30px;max-width:520px;color:var(--muted);font-size:clamp(15px,1.4vw,18px)}
.hero-actions{display:flex;gap:14px;margin-top:38px;flex-wrap:wrap}
.hero-fig{position:relative}
.hero-imgwrap{overflow:hidden;border-radius:var(--radius);aspect-ratio:4/5;background:var(--surface)}
.hero-imgwrap img{width:100%;height:100%;object-fit:cover;animation:breathe 13s ease-in-out infinite alternate}
@keyframes breathe{from{transform:scale(1)}to{transform:scale(1.07)}}
.hero-fig figcaption{margin-top:12px;color:var(--muted);display:flex;justify-content:space-between;gap:12px}
.hero-side{position:absolute;right:calc(var(--pad)*.4);top:50%;transform:rotate(90deg) translateX(-50%);transform-origin:right;color:var(--muted);opacity:.7}
.hero-scroll{position:absolute;left:var(--pad);bottom:26px;display:flex;align-items:center;gap:10px;color:var(--muted)}
.hero-scroll::after{content:"";width:52px;height:1px;background:var(--ink);transform-origin:left;animation:scan 2.2s var(--ease) infinite}
@keyframes scan{0%{transform:scaleX(0)}55%{transform:scaleX(1)}100%{transform:scaleX(1);opacity:0}}

/* ============ TICKER ============ */
.ticker{border-block:1px solid var(--line);padding:18px 0;overflow:hidden;background:var(--surface)}
.ticker-track{display:flex;width:max-content;animation:marq 32s linear infinite}
.ticker:hover .ticker-track{animation-play-state:paused}
@keyframes marq{to{transform:translateX(-50%)}}
.tick-group{display:flex;align-items:center;gap:34px;padding-right:34px}
.tick-item{font-family:var(--font-d);font-weight:600;font-size:15px;letter-spacing:.06em;text-transform:uppercase;white-space:nowrap}
.tick-sep{color:var(--accent)}

/* ============ РАБОТЫ ============ */
.works{padding:clamp(80px,10vw,140px) 0 clamp(60px,8vw,110px)}
.sec-head{display:grid;grid-template-columns:1fr auto;gap:8px 30px;align-items:end;margin-bottom:clamp(36px,5vw,64px)}
.sec-head .kicker{grid-column:1/-1}
.sec-intro{max-width:340px;color:var(--muted);font-size:14px;padding-bottom:6px}
.works-grid{display:grid;grid-template-columns:repeat(12,1fr);gap:clamp(18px,2.4vw,34px)}
.wc-7{grid-column:span 7}.wc-5{grid-column:span 5}
.wcard-img{position:relative;display:block;overflow:hidden;border-radius:var(--radius);background:var(--surface)}
.wc-7 .wcard-img{aspect-ratio:16/10}
.wc-5 .wcard-img{aspect-ratio:4/3}
.wcard-img img{width:100%;height:100%;object-fit:cover;transition:transform 1s var(--ease)}
.wcard:hover .wcard-img img{transform:scale(1.06)}
.wcard-tag{position:absolute;top:14px;left:14px;background:color-mix(in srgb,var(--bg) 92%,transparent);backdrop-filter:blur(6px);padding:7px 12px;border-radius:var(--btn-r)}
.wcard-view{position:absolute;left:0;right:0;bottom:0;background:var(--ink);color:var(--bg);padding:13px 18px;font-family:'IBM Plex Mono',monospace;font-size:11px;letter-spacing:.14em;text-transform:uppercase;transform:translateY(101%);transition:transform .45s var(--ease)}
.wcard:hover .wcard-view{transform:none}
.wcard-info{padding-top:18px;display:grid;grid-template-columns:auto 1fr;gap:4px 16px;align-items:baseline}
.wcard-info .idx{color:var(--accent)}
.wcard-info h3{font-size:clamp(20px,2vw,27px)}
.wcard-meta{grid-column:2;color:var(--muted)}
.wcard-desc{grid-column:2;color:var(--muted);font-size:14px;max-width:480px}
/* карточка-статистика */
.stat{background:var(--panel);color:var(--panel-ink);border-radius:var(--radius);padding:clamp(26px,3vw,44px);display:flex;flex-direction:column;gap:26px;justify-content:space-between;min-height:100%}
.stat-k{opacity:.65}
.stat-nums{display:grid;grid-template-columns:repeat(3,1fr);gap:18px}
.stat-i .num{font-family:var(--font-d);font-weight:700;font-size:clamp(34px,4.4vw,64px);line-height:1;display:block;letter-spacing:-.02em}
.stat-l{font-family:'IBM Plex Mono',monospace;font-size:11px;letter-spacing:.12em;text-transform:uppercase;opacity:.6;display:block;margin-top:10px}
.stat-link{font-family:'IBM Plex Mono',monospace;font-size:11px;letter-spacing:.14em;text-transform:uppercase;color:var(--panel-ink);opacity:.85;display:inline-flex;gap:8px;align-items:center;transition:gap .3s}
.stat-link:hover{gap:14px;color:var(--accent)}
body[data-theme="digital"] .stat-link:hover{color:#7C97FF}

/* ============ СТУДИЯ ============ */
.studio{background:var(--surface);border-block:1px solid var(--line);padding:clamp(80px,10vw,140px) 0}
.studio-grid{display:grid;grid-template-columns:minmax(0,4fr) minmax(0,7fr);gap:clamp(30px,5vw,90px)}
.studio-left{position:relative}
.studio-mark{position:sticky;top:110px;font-family:var(--font-d);font-weight:800;font-size:clamp(70px,9vw,140px);line-height:.9;color:transparent;-webkit-text-stroke:1.5px var(--ink);letter-spacing:-.02em}
.studio-mark sup{font-size:.22em;-webkit-text-stroke:1px var(--ink)}
.studio-note{display:block;margin-top:18px;color:var(--muted);position:sticky;top:calc(110px + clamp(80px,10vw,150px))}
.studio-lead{font-family:var(--font-d);font-weight:500;font-size:clamp(19px,2.2vw,27px);line-height:1.35;margin:22px 0 18px}
body[data-theme="atelier"] .studio-lead{font-style:italic}
.studio-text{color:var(--muted);max-width:600px}
.facts{margin-top:38px;border-top:1px solid var(--line)}
.fact{display:flex;justify-content:space-between;gap:20px;padding:14px 0;border-bottom:1px solid var(--line);font-size:14px}
.fact dt{color:var(--muted)}
.fact dd{font-weight:600;text-align:right}

/* ============ УСЛУГИ ============ */
.services{padding:clamp(80px,10vw,140px) 0}
.svc-list{border-top:1px solid var(--line)}
.svc{border-bottom:1px solid var(--line)}
.svc-head{width:100%;display:grid;grid-template-columns:70px 1fr 44px;align-items:center;gap:16px;padding:26px 0;text-align:left}
.svc-idx{color:var(--muted)}
.svc-title{font-family:var(--font-d);font-weight:700;font-size:clamp(19px,2.6vw,30px);transition:color .3s,transform .4s var(--ease)}
.svc-head:hover .svc-title{color:var(--accent);transform:translateX(8px)}
.svc-plus{position:relative;width:18px;height:18px;justify-self:end}
.svc-plus::before,.svc-plus::after{content:"";position:absolute;background:var(--ink);transition:transform .4s var(--ease)}
.svc-plus::before{left:0;right:0;top:8px;height:2px}
.svc-plus::after{top:0;bottom:0;left:8px;width:2px}
.svc.open .svc-plus::after{transform:scaleY(0)}
.svc.open .svc-plus::before{transform:rotate(180deg)}
.svc-body{overflow:hidden;max-height:0;transition:max-height .5s var(--ease)}
.svc-in{padding:0 0 30px 86px;max-width:760px}
.svc-in p{color:var(--muted)}
.chips{display:flex;flex-wrap:wrap;gap:8px;margin-top:16px}
.chip{font-family:'IBM Plex Mono',monospace;font-size:11px;letter-spacing:.1em;text-transform:uppercase;border:1px solid var(--line);border-radius:999px;padding:7px 14px;color:var(--muted);background:var(--surface)}

/* ============ КЛИЕНТЫ ============ */
.clients{padding:0 0 clamp(80px,10vw,140px)}
.cl-grid{display:grid;grid-template-columns:repeat(4,1fr);border-top:1px solid var(--line)}
.cl-item{border-bottom:1px solid var(--line);padding:26px 18px;display:flex;flex-direction:column;gap:10px;transition:background .35s}
.cl-item:nth-child(odd){border-right:1px solid var(--line)}
.cl-item:nth-child(4n+2),.cl-item:nth-child(4n+3){border-right:1px solid var(--line)}
.cl-item:hover{background:var(--accent-soft)}
.cl-idx{color:var(--muted)}
.cl-name{font-family:var(--font-d);font-weight:600;font-size:clamp(17px,1.8vw,24px);transition:transform .4s var(--ease),color .3s}
.cl-item:hover .cl-name{transform:translateX(6px);color:var(--accent)}

/* ============ CTA ============ */
.cta{background:var(--panel);color:var(--panel-ink);padding:clamp(90px,11vw,160px) 0;position:relative;overflow:hidden}
.cta .kicker{opacity:.6}
.cta-title{font-size:clamp(40px,7vw,96px);letter-spacing:-.02em;margin:24px 0 20px;max-width:900px}
.cta-text{opacity:.72;max-width:520px}
.cta-row{display:flex;align-items:center;gap:26px;margin-top:44px;flex-wrap:wrap}
.cta-mail{font-size:13px;opacity:.85;border-bottom:1px solid color-mix(in srgb,var(--panel-ink) 40%,transparent);padding-bottom:3px;transition:border-color .3s,opacity .3s}
.cta-mail:hover{opacity:1;border-color:var(--accent)}
.cta-badge{position:absolute;right:clamp(20px,6vw,90px);top:50%;translate:0 -50%;width:150px;height:150px;color:var(--panel-ink);opacity:.9}
.cta-badge svg{width:100%;height:100%}
.cta-badge .rot{animation:spin 18s linear infinite;transform-origin:50% 50%}
@keyframes spin{to{transform:rotate(360deg)}}
.cta-badge text{font-family:'IBM Plex Mono',monospace;font-size:10.5px;letter-spacing:2.5px;fill:currentColor}
.cta-badge .arr{stroke:currentColor;stroke-width:2;fill:none}

/* ============ FOOTER ============ */
.footer{padding:clamp(50px,7vw,90px) 0 30px;overflow:hidden}
.foot-word{font-family:var(--font-d);font-weight:800;font-size:clamp(110px,26vw,400px);line-height:.82;letter-spacing:-.02em;color:transparent;-webkit-text-stroke:1px var(--ink);opacity:.85;text-align:center;user-select:none;transition:color .6s}
.foot-word:hover{color:var(--ink)}
.foot-grid{display:grid;grid-template-columns:2fr 1fr 1fr 1fr;gap:30px;margin-top:clamp(40px,6vw,80px)}
.foot-grid h4{font-family:'IBM Plex Mono',monospace;font-size:11px;letter-spacing:.14em;text-transform:uppercase;color:var(--muted);font-weight:500;margin-bottom:16px}
.foot-grid a{display:block;padding:4px 0;font-size:14px;color:var(--muted);transition:color .3s,transform .3s}
.foot-grid a:hover{color:var(--accent);transform:translateX(4px)}
.foot-desc{color:var(--muted);font-size:14px;max-width:300px}
.foot-bottom{margin-top:clamp(40px,5vw,70px);padding-top:22px;border-top:1px solid var(--line);display:flex;flex-wrap:wrap;gap:10px 26px;color:var(--muted);justify-content:space-between}

/* ============ WIPE ============ */
#wipe{position:fixed;inset:0;z-index:3000;pointer-events:none;overflow:hidden}
.wipe-panel{position:absolute;inset:0;background:var(--ink);color:var(--bg);display:flex;align-items:center;justify-content:center;gap:18px;transform:translateY(101%);transition:transform .55s cubic-bezier(.85,0,.15,1)}
#wipe.up .wipe-panel{transform:translateY(0)}
#wipe.out .wipe-panel{transform:translateY(-101%)}
.wipe-mark{font-family:var(--font-d);font-weight:800;font-size:clamp(50px,10vw,120px);letter-spacing:-.02em}
.wipe-no{font-family:'IBM Plex Mono',monospace;font-size:clamp(14px,2vw,22px);color:var(--accent);align-self:flex-end;padding-bottom:clamp(14px,2vw,26px)}
body[data-theme="atelier"] .wipe-no{color:#D9B36A}

/* ============ АДАПТИВ ============ */
@media (max-width:1100px){
  .switch-btn b{display:none}
  .nav-desktop{display:none}
  .burger{display:block}
}
@media (max-width:960px){
  .hero-grid{grid-template-columns:1fr;gap:34px}
  .hero{min-height:auto;padding-top:calc(var(--hdr-h) + 44px)}
  .hero-imgwrap{aspect-ratio:4/3}
  .hero-side{display:none}
  .works-grid{grid-template-columns:1fr}
  .wc-7,.wc-5{grid-column:auto}
  .studio-grid{grid-template-columns:1fr}
  .studio-mark{position:static;font-size:clamp(70px,16vw,120px)}
  .studio-note{position:static}
  .cl-grid{grid-template-columns:repeat(2,1fr)}
  .cl-item:nth-child(odd){border-right:1px solid var(--line)}
  .cl-item:nth-child(4n+2),.cl-item:nth-child(4n+3){border-right:0}
  .foot-grid{grid-template-columns:1fr 1fr}
  .cta-badge{display:none}
  .hdr-cta{display:none}
}
@media (max-width:640px){
  .svc-head{grid-template-columns:40px 1fr 30px}
  .svc-in{padding-left:0}
  .stat-nums{grid-template-columns:1fr;gap:22px}
  .sec-head{grid-template-columns:1fr}
  .sec-intro{max-width:100%}
  .hero-actions .btn{width:100%;justify-content:center}
  .foot-grid{grid-template-columns:1fr}
}

/* ============ REDUCED MOTION ============ */
@media (prefers-reduced-motion: reduce){
  html{scroll-behavior:auto}
  *,*::before,*::after{animation:none!important;transition:none!important}
  .js [data-observe]{opacity:1;transform:none}
  .js .reveal-lines:not(.in) .ln>span{transform:none}
  .ticker-track{animation:none!important;flex-wrap:wrap;width:auto}
}
`

// ── HTML body ─────────────────────────────────────────────────────────────────
const CINEMA_PORTFOLIO_HTML_BODY = `
<!-- WIPE-ПЕРЕХОД МЕЖДУ БРЕНДАМИ -->
<div id="wipe" aria-hidden="true">
  <div class="wipe-panel"><span class="wipe-mark">BST</span><span class="wipe-no" id="wipeNo">01</span></div>
</div>

<!-- HEADER -->
<header id="hdr">
  <div class="hdr-in">
    <a class="brand" href="#top" aria-label="BST — на главную">
      <span class="brand-mark">BST</span><span class="brand-dot">·</span><span class="brand-sub" data-bind="brandSub">Capital</span>
    </a>
    <nav class="nav-desktop" aria-label="Основная навигация">
      <a href="#works">Работы</a><a href="#studio">Студия</a><a href="#services">Услуги</a><a href="#clients">Клиенты</a>
    </nav>
    <div class="hdr-right">
      <div class="switch" role="group" aria-label="Вариант бренда">
        <button class="switch-btn on" data-key="capital"><i>01</i><b>Capital</b></button>
        <button class="switch-btn" data-key="atelier"><i>02</i><b>Atelier</b></button>
        <button class="switch-btn" data-key="digital"><i>03</i><b>Digital</b></button>
        <button class="switch-btn" data-key="form"><i>04</i><b>Form</b></button>
      </div>
      <a href="#contact" class="btn btn-solid hdr-cta">Обсудить проект</a>
      <button class="burger" id="burger" aria-label="Открыть меню"><span></span><span></span></button>
    </div>
  </div>
</header>

<!-- МОБИЛЬНОЕ МЕНЮ -->
<div class="m-menu" id="mmenu">
  <nav class="m-nav" aria-label="Мобильная навигация">
    <a href="#works"><span>Работы</span><i>01</i></a>
    <a href="#studio"><span>Студия</span><i>02</i></a>
    <a href="#services"><span>Услуги</span><i>03</i></a>
    <a href="#clients"><span>Клиенты</span><i>04</i></a>
    <a href="#contact"><span>Контакты</span><i>05</i></a>
  </nav>
  <div class="switch m-switch" role="group" aria-label="Вариант бренда">
    <button class="switch-btn on" data-key="capital"><i>01</i><b>Capital</b></button>
    <button class="switch-btn" data-key="atelier"><i>02</i><b>Atelier</b></button>
    <button class="switch-btn" data-key="digital"><i>03</i><b>Digital</b></button>
    <button class="switch-btn" data-key="form"><i>04</i><b>Form</b></button>
  </div>
  <p class="m-contact">hello@bst.studio<br>+7 495 120-40-40<br>Клавиши 1–4 — смена бренда</p>
  <div class="m-brand" aria-hidden="true">BST</div>
</div>

<main id="top">

  <!-- HERO -->
  <section class="hero">
    <div class="wrap hero-grid">
      <div class="hero-copy">
        <span class="kicker mono" data-bind="heroKicker">Вариант 01 — Финансы и стратегия</span>
        <h1 class="hero-title reveal-lines in" data-bind="heroTitle"><span class="ln"><span style="--d:0">Бренды, которым</span></span><span class="ln"><span style="--d:1">доверяют <em>капитал</em></span></span></h1>
        <p class="hero-sub" data-bind="heroSub">Студия BST проектирует корпоративные айдентики, цифровые продукты и годовые отчёты для финансового сектора. Строго, системно, без лишнего.</p>
        <div class="hero-actions">
          <a class="btn btn-solid" href="#works">Смотреть работы</a>
          <a class="btn btn-ghost" href="#contact">Связаться</a>
        </div>
      </div>
      <figure class="hero-fig" data-observe>
        <div class="hero-imgwrap"><img class="t-img" data-img="hero" src="https://image.qwenlm.ai/public_source/9f288a6c-56fe-416e-8d09-e1b854812e12/14e7e5cf9-c9cd-4afc-b29f-696fa3753e70.png" alt="BST — hero-визуал бренда"></div>
        <figcaption class="mono"><span data-bind="heroMeta">Москва — Дубай · с 2013</span><span>Vol. 04</span></figcaption>
      </figure>
    </div>
    <span class="hero-side mono">Портфолио — четыре бренда, один код</span>
    <span class="hero-scroll mono">Scroll</span>
  </section>

  <!-- TICKER -->
  <div class="ticker" aria-hidden="true"><div class="ticker-track" id="tickTrack"></div></div>

  <!-- РАБОТЫ -->
  <section class="works wrap" id="works">
    <div class="sec-head" data-observe>
      <p class="kicker mono">01 · Работы</p>
      <h2 class="sec-title reveal-lines"><span class="ln"><span data-bind="worksTitle">Кейсы, которым доверяют</span></span></h2>
      <p class="sec-intro" data-bind="worksIntro">Кейсы для банков, фондов и корпораций — 2023–2025.</p>
    </div>
    <div class="works-grid">
      <article class="wcard wc-7" data-observe>
        <a class="wcard-img" href="#contact" aria-label="Смотреть кейс">
          <img class="t-img" data-img="p1" src="https://image.qwenlm.ai/public_source/9f288a6c-56fe-416e-8d09-e1b854812e12/1e85bfa70-1b29-4d33-abbf-fda643e28158.png" alt="Проект 1" loading="lazy">
          <span class="wcard-tag mono" data-bind="p1Tag">Ребрендинг · Годовой отчёт</span>
          <span class="wcard-view">Смотреть кейс →</span>
        </a>
        <div class="wcard-info">
          <span class="idx mono">01</span><h3 data-bind="p1Title">Меридиан Банк</h3>
          <p class="wcard-meta mono"><span data-bind="p1Year">2024</span></p>
          <p class="wcard-desc" data-bind="p1Desc">Новая визуальная система и годовой отчёт для банка из топ-10.</p>
        </div>
      </article>
      <article class="wcard wc-5" data-observe>
        <a class="wcard-img" href="#contact" aria-label="Смотреть кейс">
          <img class="t-img" data-img="p2" src="https://image.qwenlm.ai/public_source/9f288a6c-56fe-416e-8d09-e1b854812e12/1ec9caca2-3fb7-4985-aad4-071f6faa21f1.png" alt="Проект 2" loading="lazy">
          <span class="wcard-tag mono" data-bind="p2Tag">Финтех-платформа · UX/UI</span>
          <span class="wcard-view">Смотреть кейс →</span>
        </a>
        <div class="wcard-info">
          <span class="idx mono">02</span><h3 data-bind="p2Title">Nord Capital</h3>
          <p class="wcard-meta mono"><span data-bind="p2Year">2024</span></p>
          <p class="wcard-desc" data-bind="p2Desc">Личный кабинет инвестора: 40+ экранов и дизайн-система.</p>
        </div>
      </article>
      <article class="wcard wc-5" data-observe>
        <a class="wcard-img" href="#contact" aria-label="Смотреть кейс">
          <img class="t-img" data-img="p3" src="https://image.qwenlm.ai/public_source/9f288a6c-56fe-416e-8d09-e1b854812e12/105d2914b-fd2b-4b56-8f48-71111391ac7f.png" alt="Проект 3" loading="lazy">
          <span class="wcard-tag mono" data-bind="p3Tag">Корпоративная айдентика</span>
          <span class="wcard-view">Смотреть кейс →</span>
        </a>
        <div class="wcard-info">
          <span class="idx mono">03</span><h3 data-bind="p3Title">Атлант Групп</h3>
          <p class="wcard-meta mono"><span data-bind="p3Year">2023</span></p>
          <p class="wcard-desc" data-bind="p3Desc">Носители, деловая документация и гайдлайн на 120 страниц.</p>
        </div>
      </article>
      <aside class="stat wc-7" data-observe>
        <p class="stat-k mono" data-bind="statKicker">BST Capital в цифрах</p>
        <div class="stat-nums" data-bind="statNums">
          <div class="stat-i"><span class="num" data-count="12">12</span><span class="stat-l">лет на рынке</span></div>
          <div class="stat-i"><span class="num" data-count="140" data-suffix="+">140+</span><span class="stat-l">проектов</span></div>
          <div class="stat-i"><span class="num" data-count="9">9</span><span class="stat-l">стран</span></div>
        </div>
        <a class="stat-link" href="#studio">О студии <span>→</span></a>
      </aside>
    </div>
  </section>

  <!-- СТУДИЯ -->
  <section class="studio" id="studio">
    <div class="wrap studio-grid">
      <div class="studio-left">
        <div class="studio-mark" aria-hidden="true">BST<sup>®</sup></div>
        <span class="studio-note mono" data-bind="studioNote">Независимая студия — четыре направления</span>
      </div>
      <div class="studio-right" data-observe>
        <p class="kicker mono">02 · Студия</p>
        <h2 class="sec-title reveal-lines" style="margin-top:20px"><span class="ln"><span data-bind="aboutTitle">Дисциплина как стиль</span></span></h2>
        <p class="studio-lead" data-bind="aboutLead">Мы — студия, которая говорит на языке советов директоров: спокойно, цифрами и по делу.</p>
        <p class="studio-text" data-bind="aboutText">BST Capital — направление студии BST для финансового сектора и крупных корпораций. Мы проектируем бренды, которые проходят комплаенс, нравятся акционерам и остаются актуальными через десять лет. Минимум декора — максимум смысла.</p>
        <dl class="facts" data-bind="facts">
          <div class="fact"><dt>Основана</dt><dd>2013</dd></div>
          <div class="fact"><dt>Команда</dt><dd>32 человека</dd></div>
          <div class="fact"><dt>Офисы</dt><dd>Москва · Дубай</dd></div>
          <div class="fact"><dt>Фокус</dt><dd>Финтех · Банки · Фонды</dd></div>
        </dl>
      </div>
    </div>
  </section>

  <!-- УСЛУГИ -->
  <section class="services wrap" id="services">
    <div class="sec-head" data-observe>
      <p class="kicker mono">03 · Услуги</p>
      <h2 class="sec-title reveal-lines"><span class="ln"><span data-bind="servicesTitle">Полный цикл для корпораций</span></span></h2>
    </div>
    <div class="svc-list">
      <article class="svc" data-observe>
        <button class="svc-head" aria-expanded="false"><span class="svc-idx mono">01</span><span class="svc-title" data-bind="s1Title">Стратегия и аудит</span><span class="svc-plus"></span></button>
        <div class="svc-body"><div class="svc-in"><p data-bind="s1Desc">Позиционирование, архитектура бренда, исследования и дорожная карта внедрения.</p><div class="chips" data-bind="s1Chips"><span class="chip">Позиционирование</span><span class="chip">Аналитика</span><span class="chip">Нейминг</span></div></div></div>
      </article>
      <article class="svc" data-observe>
        <button class="svc-head" aria-expanded="false"><span class="svc-idx mono">02</span><span class="svc-title" data-bind="s2Title">Корпоративная айдентика</span><span class="svc-plus"></span></button>
        <div class="svc-body"><div class="svc-in"><p data-bind="s2Desc">Логотип, визуальный язык, гайдлайны и носители — от визитки до годового отчёта.</p><div class="chips" data-bind="s2Chips"><span class="chip">Логотип</span><span class="chip">Гайдлайн</span><span class="chip">Носители</span></div></div></div>
      </article>
      <article class="svc" data-observe>
        <button class="svc-head" aria-expanded="false"><span class="svc-idx mono">03</span><span class="svc-title" data-bind="s3Title">Цифровые продукты</span><span class="svc-plus"></span></button>
        <div class="svc-body"><div class="svc-in"><p data-bind="s3Desc">Сайты, личные кабинеты и дизайн-системы для банков и финтеха.</p><div class="chips" data-bind="s3Chips"><span class="chip">UX/UI</span><span class="chip">Дизайн-системы</span><span class="chip">Frontend</span></div></div></div>
      </article>
      <article class="svc" data-observe>
        <button class="svc-head" aria-expanded="false"><span class="svc-idx mono">04</span><span class="svc-title" data-bind="s4Title">Годовые отчёты</span><span class="svc-plus"></span></button>
        <div class="svc-body"><div class="svc-in"><p data-bind="s4Desc">Инвесторские презентации и отчёты, которым верят акционеры.</p><div class="chips" data-bind="s4Chips"><span class="chip">Print</span><span class="chip">Инфографика</span><span class="chip">ESG</span></div></div></div>
      </article>
    </div>
  </section>

  <!-- КЛИЕНТЫ -->
  <section class="clients" id="clients">
    <div class="wrap">
      <div class="sec-head" data-observe>
        <p class="kicker mono">04 · Клиенты</p>
        <h2 class="sec-title reveal-lines"><span class="ln"><span data-bind="clientsTitle">Нам доверяют</span></span></h2>
      </div>
      <ul class="cl-grid" id="clGrid"></ul>
    </div>
  </section>

  <!-- CTA -->
  <section class="cta" id="contact">
    <div class="wrap" data-observe>
      <p class="kicker mono" data-bind="ctaKicker">Контакты</p>
      <h2 class="cta-title reveal-lines" data-bind="ctaTitle"><span class="ln"><span>Обсудим ваш проект</span></span></h2>
      <p class="cta-text" data-bind="ctaText">Расскажите о задаче — вернёмся с планом и оценкой в течение 48 часов.</p>
      <div class="cta-row">
        <a class="btn btn-accent" href="mailto:hello@bst.studio">Написать</a>
        <a class="cta-mail mono" href="mailto:hello@bst.studio">hello@bst.studio</a>
      </div>
    </div>
    <div class="cta-badge" aria-hidden="true">
      <svg viewBox="0 0 120 120">
        <defs><path id="circ" d="M60,60 m-46,0 a46,46 0 1,1 92,0 a46,46 0 1,1 -92,0"/></defs>
        <g class="rot"><text><textPath href="#circ">BST STUDIO • PORTFOLIO • BST STUDIO • PORTFOLIO •&#160;</textPath></text></g>
        <path class="arr" d="M44 60h30M64 50l10 10-10 10"/>
      </svg>
    </div>
  </section>
</main>

<!-- FOOTER -->
<footer class="footer">
  <div class="wrap">
    <div class="foot-word" aria-hidden="true">BST</div>
    <div class="foot-grid">
      <div>
        <h4>BST Studio</h4>
        <p class="foot-desc" data-bind="footDesc">Четыре бренда в одном коде: корпоративный, премиальный, технологический и архитектурный. Единая студия — разные языки.</p>
      </div>
      <div>
        <h4>Навигация</h4>
        <a href="#works">Работы</a><a href="#studio">Студия</a><a href="#services">Услуги</a><a href="#clients">Клиенты</a>
      </div>
      <div>
        <h4>Соцсети</h4>
        <a href="#" rel="noopener">Behance</a><a href="#" rel="noopener">Dribbble</a><a href="#" rel="noopener">Telegram</a><a href="#" rel="noopener">VK</a>
      </div>
      <div>
        <h4>Контакты</h4>
        <a href="mailto:hello@bst.studio">hello@bst.studio</a>
        <a href="tel:+74951204040">+7 495 120-40-40</a>
      </div>
    </div>
    <div class="foot-bottom mono">
      <span>© <span id="yr">2025</span> BST Studio</span>
      <span>Вариант <span data-bind="variantNo">01</span> · <span data-bind="brandSub">Capital</span></span>
      <span>4 бренда — один код</span>
    </div>
  </div>
</footer>
`

// ── Engine JS ─────────────────────────────────────────────────────────────────
function cinemaPortfolioEngineJs(brand: string, mode: 'canvas' | 'preview'): string {
  return `
/* ================= ДАННЫЕ ЧЕТЫРЁХ БРЕНДОВ ================= */
var IMG = {
  capital:{
    hero:"https://image.qwenlm.ai/public_source/9f288a6c-56fe-416e-8d09-e1b854812e12/14e7e5cf9-c9cd-4afc-b29f-696fa3753e70.png",
    p1:"https://image.qwenlm.ai/public_source/9f288a6c-56fe-416e-8d09-e1b854812e12/1e85bfa70-1b29-4d33-abbf-fda643e28158.png",
    p2:"https://image.qwenlm.ai/public_source/9f288a6c-56fe-416e-8d09-e1b854812e12/1ec9caca2-3fb7-4985-aad4-071f6faa21f1.png",
    p3:"https://image.qwenlm.ai/public_source/9f288a6c-56fe-416e-8d09-e1b854812e12/105d2914b-fd2b-4b56-8f48-71111391ac7f.png"
  },
  atelier:{
    hero:"https://image.qwenlm.ai/public_source/9f288a6c-56fe-416e-8d09-e1b854812e12/1127c1953-8488-4202-8b56-6ae9dee569e2.png",
    p1:"https://image.qwenlm.ai/public_source/9f288a6c-56fe-416e-8d09-e1b854812e12/180004fd9-d668-4735-bf2d-a256dfbeee5d.png",
    p2:"https://image.qwenlm.ai/public_source/9f288a6c-56fe-416e-8d09-e1b854812e12/117f87e37-7d83-4e09-86bf-16ce4b73a76f.png",
    p3:"https://image.qwenlm.ai/public_source/9f288a6c-56fe-416e-8d09-e1b854812e12/164030a38-3fc6-4933-bdf7-eb43e20f06b1.png"
  },
  digital:{
    hero:"https://image.qwenlm.ai/public_source/9f288a6c-56fe-416e-8d09-e1b854812e12/1f67dd1b9-a943-4bd2-9a20-c53dcedcfb84.png",
    p1:"https://image.qwenlm.ai/public_source/9f288a6c-56fe-416e-8d09-e1b854812e12/1fe4fb56f-892b-4f04-94a8-c6364336010f.png",
    p2:"https://image.qwenlm.ai/public_source/9f288a6c-56fe-416e-8d09-e1b854812e12/19875db5f-07a7-4f6a-b065-854d4397a68f.png",
    p3:"https://image.qwenlm.ai/public_source/9f288a6c-56fe-416e-8d09-e1b854812e12/11529ffae-7492-4845-a15a-e392c738cc73.png"
  },
  form:{
    hero:"https://image.qwenlm.ai/public_source/9f288a6c-56fe-416e-8d09-e1b854812e12/15c77ce09-67db-4291-bafa-feed947aded4.png",
    p1:"https://image.qwenlm.ai/public_source/9f288a6c-56fe-416e-8d09-e1b854812e12/17b42d98e-db34-404a-a8b4-8db6bc864b03.png",
    p2:"https://image.qwenlm.ai/public_source/9f288a6c-56fe-416e-8d09-e1b854812e12/139d9501f-5192-4ba2-b786-caaa67e9f806.png",
    p3:"https://image.qwenlm.ai/public_source/9f288a6c-56fe-416e-8d09-e1b854812e12/15a008731-d729-4ac1-a66d-93c464917df7.png"
  }
};

var THEMES = {
  capital:{
    no:"01", doc:"BST Capital — портфолио студии",
    ticker:["Стратегия","Айдентика","Годовые отчёты","Digital-продукты","Бренд-аудит","Аналитика"],
    clients:["Меридиан Банк","Nord Capital","Атлант Групп","Вектор Фонд","Прайм Банк","Дельта Страхование","Гранит Холдинг","Синергия"],
    bind:{
      brandSub:"Capital", variantNo:"01",
      heroKicker:"Вариант 01 — Финансы и стратегия",
      heroTitle:'<span class="ln"><span style="--d:0">Бренды, которым</span></span><span class="ln"><span style="--d:1">доверяют <em>капитал</em></span></span>',
      heroSub:"Студия BST проектирует корпоративные айдентики, цифровые продукты и годовые отчёты для финансового сектора. Строго, системно, без лишнего.",
      heroMeta:"Москва — Дубай · с 2013",
      worksTitle:"Кейсы, которым доверяют",
      worksIntro:"Кейсы для банков, фондов и корпораций — 2023–2025.",
      p1Title:"Меридиан Банк", p1Tag:"Ребрендинг · Годовой отчёт", p1Year:"2024", p1Desc:"Новая визуальная система и годовой отчёт для банка из топ-10.",
      p2Title:"Nord Capital", p2Tag:"Финтех-платформа · UX/UI", p2Year:"2024", p2Desc:"Личный кабинет инвестора: 40+ экранов и дизайн-система.",
      p3Title:"Атлант Групп", p3Tag:"Корпоративная айдентика", p3Year:"2023", p3Desc:"Носители, деловая документация и гайдлайн на 120 страниц.",
      statKicker:"BST Capital в цифрах",
      statNums:'<div class="stat-i"><span class="num" data-count="12">0</span><span class="stat-l">лет на рынке</span></div><div class="stat-i"><span class="num" data-count="140" data-suffix="+">0</span><span class="stat-l">проектов</span></div><div class="stat-i"><span class="num" data-count="9">0</span><span class="stat-l">стран</span></div>',
      studioNote:"Независимая студия — четыре направления",
      aboutTitle:"Дисциплина как стиль",
      aboutLead:"Мы — студия, которая говорит на языке советов директоров: спокойно, цифрами и по делу.",
      aboutText:"BST Capital — направление студии BST для финансового сектора и крупных корпораций. Мы проектируем бренды, которые проходят комплаенс, нравятся акционерам и остаются актуальными через десять лет. Минимум декора — максимум смысла.",
      facts:'<div class="fact"><dt>Основана</dt><dd>2013</dd></div><div class="fact"><dt>Команда</dt><dd>32 человека</dd></div><div class="fact"><dt>Офисы</dt><dd>Москва · Дубай</dd></div><div class="fact"><dt>Фокус</dt><dd>Финтех · Банки · Фонды</dd></div>',
      servicesTitle:"Полный цикл для корпораций",
      s1Title:"Стратегия и аудит", s1Desc:"Позиционирование, архитектура бренда, исследования и дорожная карта внедрения.", s1Chips:'<span class="chip">Позиционирование</span><span class="chip">Аналитика</span><span class="chip">Нейминг</span>',
      s2Title:"Корпоративная айдентика", s2Desc:"Логотип, визуальный язык, гайдлайны и носители — от визитки до годового отчёта.", s2Chips:'<span class="chip">Логотип</span><span class="chip">Гайдлайн</span><span class="chip">Носители</span>',
      s3Title:"Цифровые продукты", s3Desc:"Сайты, личные кабинеты и дизайн-системы для банков и финтеха.", s3Chips:'<span class="chip">UX/UI</span><span class="chip">Дизайн-системы</span><span class="chip">Frontend</span>',
      s4Title:"Годовые отчёты", s4Desc:"Инвесторские презентации и отчёты, которым верят акционеры.", s4Chips:'<span class="chip">Print</span><span class="chip">Инфографика</span><span class="chip">ESG</span>',
      clientsTitle:"Нам доверяют",
      ctaKicker:"Контакты",
      ctaTitle:'<span class="ln"><span>Обсудим ваш проект</span></span>',
      ctaText:"Расскажите о задаче — вернёмся с планом и оценкой в течение 48 часов.",
      footDesc:"Четыре бренда в одном коде: корпоративный, премиальный, технологический и архитектурный. Единая студия — разные языки."
    }
  },
  atelier:{
    no:"02", doc:"BST Atelier — портфолио студии",
    ticker:["Art Direction","Editorial","Campaign","Packaging","Retail","Lookbook"],
    clients:["Maison Ligne","Sève Parfum","Édition Quatre","Velours","Noir Studio","Camélia","Forme","Atelier 9"],
    bind:{
      brandSub:"Atelier", variantNo:"02",
      heroKicker:"Вариант 02 — Мода и премиум",
      heroTitle:'<span class="ln"><span style="--d:0">Тишина —</span></span><span class="ln"><span style="--d:1">новая <em>роскошь</em></span></span>',
      heroSub:"Арт-дирекшн, editorial-съёмки и упаковка для модных домов и парфюмерных брендов. Эстетика, выверенная до миллиметра.",
      heroMeta:"Париж — Милан · est. 2016",
      worksTitle:"Съёмки и запуски",
      worksIntro:"Editorial, упаковка и кампании для модных домов — 2024–2025.",
      p1Title:"Maison Ligne", p1Tag:"Lookbook · Editorial", p1Year:"2025", p1Desc:"Съёмка и дизайн лукбука на 96 полос для недели моды.",
      p2Title:"Sève Parfum", p2Tag:"Упаковка · Запуск", p2Year:"2024", p2Desc:"Флакон, коробка и кампания для нишевого парфюмерного дома.",
      p3Title:"Édition Quatre", p3Tag:"Журнал · Art Direction", p3Year:"2024", p3Desc:"Арт-дирекшн независимого издания о моде и предметной культуре.",
      statKicker:"BST Atelier в цифрах",
      statNums:'<div class="stat-i"><span class="num" data-count="60" data-suffix="+">0</span><span class="stat-l">съёмок</span></div><div class="stat-i"><span class="num" data-count="18">0</span><span class="stat-l">обложек</span></div><div class="stat-i"><span class="num" data-count="5">0</span><span class="stat-l">стран</span></div>',
      studioNote:"Ателье — четыре ремесла под одной крышей",
      aboutTitle:"Эстетика как стратегия",
      aboutLead:"Ателье BST работает на стыке моды, фотографии и предметной культуры.",
      aboutText:"Мы снимаем кампании, верстаем журналы и создаём упаковку для брендов, для которых эстетика — главный аргумент. Каждая деталь — от кернинга до тиснения — проходит ручную проверку в студии.",
      facts:'<div class="fact"><dt>Основана</dt><dd>2016</dd></div><div class="fact"><dt>Команда</dt><dd>14 человек</dd></div><div class="fact"><dt>Студии</dt><dd>Париж · Милан</dd></div><div class="fact"><dt>Фокус</dt><dd>Fashion · Beauty · Premium</dd></div>',
      servicesTitle:"Ремесло ателье",
      s1Title:"Арт-дирекшн", s1Desc:"Концепция, кастинг, референсы и полный контроль съёмочного дня.", s1Chips:'<span class="chip">Концепция</span><span class="chip">Кастинг</span><span class="chip">Съёмка</span>',
      s2Title:"Editorial и кампании", s2Desc:"Лукбуки, обложки, рекламные кампании и digital-сторителлинг.", s2Chips:'<span class="chip">Lookbook</span><span class="chip">Campaign</span><span class="chip">Social</span>',
      s3Title:"Упаковка и предмет", s3Desc:"Флаконы, коробки, тиснение и материалы, которые хочется трогать.", s3Chips:'<span class="chip">Packaging</span><span class="chip">Print</span><span class="chip">Materials</span>',
      s4Title:"Ритейл-дизайн", s4Desc:"Витрины, поп-апы и оформление бутиков в единой эстетике.", s4Chips:'<span class="chip">Retail</span><span class="chip">Pop-up</span><span class="chip">VM</span>',
      clientsTitle:"Дома и друзья ателье",
      ctaKicker:"Контакты",
      ctaTitle:'<span class="ln"><span>Создадим <em>красивую</em> историю</span></span>',
      ctaText:"Съёмка, запуск или переосмысление бренда — начнём с чашки кофе в студии.",
      footDesc:"Четыре бренда в одном коде: корпоративный, премиальный, технологический и архитектурный. Единая студия — разные языки."
    }
  },
  digital:{
    no:"03", doc:"BST Digital — портфолио студии",
    ticker:["Product Design","UX-исследования","Дизайн-системы","Motion","Frontend","AI"],
    clients:["Orbita","Pulse Health","Vertex AI","Nimbus","Kraft Labs","Echo","Byte & Co","Signal"],
    bind:{
      brandSub:"Digital", variantNo:"03",
      heroKicker:"Вариант 03 — Технологии и продукты",
      heroTitle:'<span class="ln"><span style="--d:0">Дизайн, который</span></span><span class="ln"><span style="--d:1"><em>масштабируется</em></span></span>',
      heroSub:"Продуктовый дизайн, дизайн-системы и фронтенд для SaaS и стартапов. От исследования до релиза — одной командой.",
      heroMeta:"Берлин — Лиссабон · с 2018",
      worksTitle:"Продукты в продакшене",
      worksIntro:"SaaS, мобильные приложения и AI-сервисы — 2024–2025.",
      p1Title:"Orbita CRM", p1Tag:"SaaS · Дизайн-система", p1Year:"2025", p1Desc:"120+ компонентов, тёмная тема и токены для шести продуктов.",
      p2Title:"Pulse Health", p2Tag:"Мобильное приложение", p2Year:"2024", p2Desc:"iOS и Android: трекинг здоровья, рейтинг 4.8 в сторах.",
      p3Title:"Vertex AI", p3Tag:"Промо-сайт · 3D", p3Year:"2025", p3Desc:"WebGL-сцены и новый лендинг: конверсия +38%.",
      statKicker:"BST Digital в цифрах",
      statNums:'<div class="stat-i"><span class="num" data-count="30" data-suffix="+">0</span><span class="stat-l">продуктов</span></div><div class="stat-i"><span class="num" data-count="500" data-suffix="+">0</span><span class="stat-l">экранов</span></div><div class="stat-i"><span class="num" data-count="12">0</span><span class="stat-l">наград</span></div>',
      studioNote:"Продуктовая команда внутри студии",
      aboutTitle:"Скорость без компромиссов",
      aboutLead:"BST Digital — продуктовая команда внутри студии: дизайнеры, исследователи и разработчики.",
      aboutText:"Мы запускаем SaaS-продукты и дизайн-системы быстрее, чем классические агентства готовят первую презентацию. Спринты, метрики и плотная работа с продуктовыми командами клиента.",
      facts:'<div class="fact"><dt>Основана</dt><dd>2018</dd></div><div class="fact"><dt>Команда</dt><dd>26 человек</dd></div><div class="fact"><dt>Хабы</dt><dd>Берлин · Лиссабон</dd></div><div class="fact"><dt>Фокус</dt><dd>SaaS · AI · Health</dd></div>',
      servicesTitle:"Продукт под ключ",
      s1Title:"Продуктовый дизайн", s1Desc:"От discovery и CJM до пиксель-перфект макетов и передачи в разработку.", s1Chips:'<span class="chip">UX</span><span class="chip">UI</span><span class="chip">Research</span>',
      s2Title:"Дизайн-системы", s2Desc:"Токены, компоненты и документация, которые ускоряют команды.", s2Chips:'<span class="chip">Tokens</span><span class="chip">Storybook</span><span class="chip">Docs</span>',
      s3Title:"Motion и 3D", s3Desc:"Микроанимации, промо-ролики и WebGL-сцены для продукта.", s3Chips:'<span class="chip">Motion</span><span class="chip">WebGL</span><span class="chip">3D</span>',
      s4Title:"Frontend-разработка", s4Desc:"Верстаем и разрабатываем интерфейсы на уровне макетов.", s4Chips:'<span class="chip">React</span><span class="chip">Animation</span><span class="chip">QA</span>',
      clientsTitle:"Команды-партнёры",
      ctaKicker:"Контакты",
      ctaTitle:'<span class="ln"><span>Соберём ваш <em>продукт</em></span></span>',
      ctaText:"Дизайн, система и фронтенд в одних руках. Старт спринта — через неделю.",
      footDesc:"Четыре бренда в одном коде: корпоративный, премиальный, технологический и архитектурный. Единая студия — разные языки."
    }
  },
  form:{
    no:"04", doc:"BST Form — портфолио студии",
    ticker:["Архитектура","Навигация","Выставки","Интерьер","Предмет","Генплан"],
    clients:["Галерея «Тишина»","Резиденция «Гранат»","Музей бетона","Форма Бюро","Линия 7","Дом К.","Среда","Арка"],
    bind:{
      brandSub:"Form", variantNo:"04",
      heroKicker:"Вариант 04 — Архитектура и пространство",
      heroTitle:'<span class="ln"><span style="--d:0">Форма</span></span><span class="ln"><span style="--d:1">следует <em>свету</em></span></span>',
      heroSub:"Брендинг для архитектурных бюро, навигация в пространстве и выставочные проекты. Работаем с материалом и светом.",
      heroMeta:"Санкт-Петербург · с 2015",
      worksTitle:"Пространства и системы",
      worksIntro:"Выставки, навигация и архитектура — 2023–2025.",
      p1Title:"Галерея «Тишина»", p1Tag:"Выставка · Навигация", p1Year:"2025", p1Desc:"Айдентика и wayfinding для выставки современного искусства.",
      p2Title:"Резиденция «Гранат»", p2Tag:"Архитектура · Интерьер", p2Year:"2024", p2Desc:"Частный дом 480 м²: от эскиза до авторского надзора.",
      p3Title:"Музей бетона", p3Tag:"Музей · Экспозиция", p3Year:"2023", p3Desc:"Постоянная экспозиция и графика для музея архитектуры.",
      statKicker:"BST Form в цифрах",
      statNums:'<div class="stat-i"><span class="num" data-count="40" data-suffix="+">0</span><span class="stat-l">объектов</span></div><div class="stat-i"><span class="num" data-count="12">0</span><span class="stat-l">выставок</span></div><div class="stat-i"><span class="num" data-count="7">0</span><span class="stat-l">премий</span></div>',
      studioNote:"Архитектурное направление студии",
      aboutTitle:"Материал. Свет. Порядок",
      aboutLead:"BST Form — архитектурное направление студии: пространство, свет и материал как носители бренда.",
      aboutText:"Мы проектируем навигацию, выставки и предметную среду для культурных институций и девелоперов. Работаем на стыке графического дизайна и архитектуры.",
      facts:'<div class="fact"><dt>Основана</dt><dd>2015</dd></div><div class="fact"><dt>Команда</dt><dd>18 человек</dd></div><div class="fact"><dt>Бюро</dt><dd>Санкт-Петербург</dd></div><div class="fact"><dt>Фокус</dt><dd>Культура · Девелопмент · Ритейл</dd></div>',
      servicesTitle:"Среда и графика",
      s1Title:"Брендинг пространства", s1Desc:"Айдентика, которая живёт в архитектуре: от логотипа до суперграфики.", s1Chips:'<span class="chip">Айдентика</span><span class="chip">Суперграфика</span>',
      s2Title:"Wayfinding и навигация", s2Desc:"Системы навигации для музеев, жилых кварталов и общественных пространств.", s2Chips:'<span class="chip">Навигация</span><span class="chip">Пиктограммы</span>',
      s3Title:"Выставочный дизайн", s3Desc:"Экспозиции, стенды и выставочные проекты под ключ.", s3Chips:'<span class="chip">Экспозиция</span><span class="chip">Стенды</span>',
      s4Title:"Предмет и графика", s4Desc:"Предметный дизайн, принты и малые архитектурные формы.", s4Chips:'<span class="chip">Предмет</span><span class="chip">Принт</span><span class="chip">МАФ</span>',
      clientsTitle:"Институции и бюро",
      ctaKicker:"Контакты",
      ctaTitle:'<span class="ln"><span>Построим смысл <em>вместе</em></span></span>',
      ctaText:"Пришлите бриф или просто идею — ответим в течение двух рабочих дней.",
      footDesc:"Четыре бренда в одном коде: корпоративный, премиальный, технологический и архитектурный. Единая студия — разные языки."
    }
  }
};

/* ================= ЯДРО ================= */
var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
var current = '${brand}', busy = false;
var wipe = document.getElementById('wipe');
var wipePanel = wipe.querySelector('.wipe-panel');
var wipeNo = document.getElementById('wipeNo');
var tickTrack = document.getElementById('tickTrack');
var clGrid = document.getElementById('clGrid');
var hdr = document.getElementById('hdr');

/* --- биндинг текста и картинок --- */
function doSwap(key){
  current = key;
  var t = THEMES[key];
  document.body.dataset.theme = key;
  document.title = t.doc;
  document.querySelectorAll('[data-bind]').forEach(function(el){
    var k = el.dataset.bind;
    if (t.bind[k] !== undefined) el.innerHTML = t.bind[k];
  });
  document.querySelectorAll('[data-img]').forEach(function(img){
    var src = IMG[key][img.dataset.img];
    if (src) img.src = src;
  });
  buildTicker(t.ticker);
  renderClients(t.clients);
  updateSwitch();
  replay(document.querySelector('.hero-title'));
  document.querySelectorAll('.reveal-lines.in').forEach(function(el){ replay(el); });
  document.querySelectorAll('[data-count]').forEach(function(el){ el.removeAttribute('data-done'); });
  runCounts(document.body);
  document.querySelectorAll('.svc.open .svc-body').forEach(function(b){ b.style.maxHeight = b.scrollHeight + 'px'; });
}

/* --- wipe-переход --- */
function applyTheme(key){
  if (key === current || busy) return;
  if (reduced){ doSwap(key); return; }
  busy = true;
  wipeNo.textContent = THEMES[key].no;
  wipe.classList.add('up');
  setTimeout(function(){ doSwap(key); }, 580);
  setTimeout(function(){ wipe.classList.add('out'); }, 760);
  setTimeout(function(){
    wipePanel.style.transition = 'none';
    wipe.classList.remove('up','out');
    void wipePanel.offsetWidth;
    wipePanel.style.transition = '';
    busy = false;
  }, 1400);
}

/* --- переключатели --- */
function updateSwitch(){
  document.querySelectorAll('.switch-btn').forEach(function(b){ b.classList.toggle('on', b.dataset.key === current); });
}
document.addEventListener('click', function(e){
  var btn = e.target.closest('.switch-btn');
  if (btn) applyTheme(btn.dataset.key);
});
document.addEventListener('keydown', function(e){
  var map = {'1':'capital','2':'atelier','3':'digital','4':'form'};
  if (map[e.key]) applyTheme(map[e.key]);
});

/* --- ticker --- */
function buildTicker(items){
  var g = items.concat(items).map(function(x){ return '<span class="tick-item">'+x+'</span><span class="tick-sep">•</span>'; }).join('');
  tickTrack.innerHTML = '<div class="tick-group">'+g+'</div><div class="tick-group">'+g+'</div>';
}

/* --- клиенты --- */
function renderClients(arr){
  clGrid.innerHTML = arr.map(function(n,i){ return '<li class="cl-item" data-observe><span class="cl-idx mono">0'+(i+1)+'</span><span class="cl-name">'+n+'</span></li>'; }).join('');
  observeAll();
}

/* --- reveal + счётчики --- */
var io = new IntersectionObserver(function(entries){
  entries.forEach(function(en){
    if (en.isIntersecting){
      en.target.classList.add('in');
      runCounts(en.target);
      io.unobserve(en.target);
    }
  });
},{threshold:.15});
function observeAll(){
  document.querySelectorAll('[data-observe]:not(.obs)').forEach(function(el){ el.classList.add('obs'); io.observe(el); });
  document.querySelectorAll('.reveal-lines:not(.obs)').forEach(function(el){ el.classList.add('obs'); io.observe(el); });
}
function runCounts(scope){
  scope.querySelectorAll('[data-count]').forEach(function(el){
    if (el.dataset.done) return;
    el.dataset.done = '1';
    var target = parseInt(el.dataset.count,10);
    var suf = el.dataset.suffix || '';
    if (reduced){ el.textContent = target + suf; return; }
    var start = performance.now(), dur = 1300;
    (function frame(now){
      var p = Math.min(1,(now-start)/dur);
      var e2 = 1 - Math.pow(1-p,3);
      el.textContent = Math.round(target*e2) + suf;
      if (p < 1) requestAnimationFrame(frame);
    })(start);
  });
}
function replay(el){
  if (!el) return;
  el.classList.remove('in');
  void el.offsetWidth;
  el.classList.add('in');
}

/* --- аккордеон услуг --- */
document.querySelectorAll('.svc').forEach(function(item,i){
  var head = item.querySelector('.svc-head');
  var body = item.querySelector('.svc-body');
  head.addEventListener('click', function(){
    var open = item.classList.contains('open');
    document.querySelectorAll('.svc.open').forEach(function(o){
      o.classList.remove('open');
      o.querySelector('.svc-head').setAttribute('aria-expanded','false');
      o.querySelector('.svc-body').style.maxHeight = '0px';
    });
    if (!open){
      item.classList.add('open');
      head.setAttribute('aria-expanded','true');
      body.style.maxHeight = body.scrollHeight + 'px';
    }
  });
  if (i === 0){ item.classList.add('open'); head.setAttribute('aria-expanded','true'); requestAnimationFrame(function(){ body.style.maxHeight = body.scrollHeight + 'px'; }); }
});
window.addEventListener('resize', function(){
  document.querySelectorAll('.svc.open .svc-body').forEach(function(b){ b.style.maxHeight = b.scrollHeight + 'px'; });
});

/* --- шапка при скролле --- */
window.addEventListener('scroll', function(){ hdr.classList.toggle('scrolled', window.scrollY > 12); }, {passive:true});

/* --- мобильное меню --- */
var burger = document.getElementById('burger');
var mmenu = document.getElementById('mmenu');
burger.addEventListener('click', function(){
  var open = mmenu.classList.toggle('open');
  document.body.classList.toggle('menu-open', open);
  document.body.style.overflow = open ? 'hidden' : '';
});
mmenu.querySelectorAll('a').forEach(function(a){ a.addEventListener('click', function(){
  mmenu.classList.remove('open');
  document.body.classList.remove('menu-open');
  document.body.style.overflow = '';
}); });

/* --- init --- */
document.getElementById('yr').textContent = new Date().getFullYear();
document.body.dataset.theme = '${brand}';
buildTicker(THEMES['${brand}'].ticker);
renderClients(THEMES['${brand}'].clients);
updateSwitch();
observeAll();
window.addEventListener('load', function(){
  replay(document.querySelector('.hero-title'));
  Object.values(IMG).forEach(function(set){ Object.values(set).forEach(function(u){ var im = new Image(); im.src = u; }); });
});
${mode === 'canvas' ? `(function(){
  document.documentElement.classList.add('js');
  document.querySelectorAll('[data-observe],[data-rv],.rv,.reveal,.reveal-lines,.ln').forEach(function(el){el.classList.add('in');var i=el.querySelector('.ln-inner');if(i){i.style.transform='none';i.style.opacity='1';}});
  document.body.classList.add('loaded','is-ready');
  document.addEventListener('click',function(e){var a=e.target.closest('a[href]');if(a)e.preventDefault();},true);
})();` : ''}
`
}

// ── Public API ────────────────────────────────────────────────────────────────
export function getCinemaPortfolioPageHtml(data: CinemaPortfolioPageData, mode: 'canvas' | 'preview'): string {
  const brand = data.brand ?? 'capital'
  return `<!DOCTYPE html>
<html lang="ru">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>BST Portfolio</title>
<script>document.documentElement.classList.add('js');<\/script>
<style>${CINEMA_PORTFOLIO_CSS}</style>
</head>
<body data-theme="${brand}">
${CINEMA_PORTFOLIO_HTML_BODY}
<script>${cinemaPortfolioEngineJs(brand, mode)}<\/script>
</body>
</html>`
}
